import { Period } from 'screens/Analytics/constants'
import { MetricSnapshot } from './externalIntegrations/types'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { reportError } from 'utils/error'
import { buildErrorMessage } from 'utils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useQuery, UseQueryOptions } from 'react-query'

interface Data {
  metric_snapshots: MetricSnapshot[]
}

export const fetchMetricSnapshots = async ({
  metricName,
  period,
  workspaceId,
  externalIntegrationId,
}: {
  metricName: string
  period: Period
  workspaceId: number
  externalIntegrationId: number
}) => {
  const response = await sendToApi<Data>(
    `/w/${workspaceId}/metric_snapshots?metric_name=${metricName}&period=${period}&external_integration_id=${externalIntegrationId}
    `,
    {
      method: 'GET',
    },
  )

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    reportError('Failed to fetch metric snapshots', {
      metaData: {
        response: response.data,
      },
    })
    throw new Error(
      buildErrorMessage(response.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }
  return response.data
}

export const useMetricSnapshots = (
  {
    metricName,
    period,
    externalIntegrationId,
    queryKey,
  }: {
    metricName: string
    period: Period
    externalIntegrationId: number
    queryKey?: string[]
  },
  options?: UseQueryOptions<Data, Error>,
) => {
  const workspaceId = useCurrentWorkspaceId()

  return useQuery<Data, Error>(
    queryKey || ['metric_snapshots', metricName, period, externalIntegrationId],
    () => fetchMetricSnapshots({ metricName, period, workspaceId, externalIntegrationId }),
    {
      enabled: !!workspaceId && !!externalIntegrationId && !!metricName,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      ...options,
    },
  )
}
